/**
 * 选择优惠券
 */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Form, Modal, Tabs } from 'antd';
import { connect } from 'react-redux';
import { get, set } from 'lodash';
import { GuestInitialStateProps, Icoupon } from '@/redux/reducers/guest';
import {
  getOptionalWangyuCouponList,
  getOptionalManualCouponList,
  saveData,
} from '@/redux/actions/guest';
import { fetchCouponCalculate, fetchDesensitizedInfoByWyuid } from '@/api/guest';
import { CouponStatus, OccType, SourceChannelType } from '@/config';
import { ItabItem, renderIntlLabel } from '@/utils';
import { isNil } from '@/utils/number';
import WangyuCouponList from './component/WangyuCouponList';
import XuanzhuCouponList from './component/XuanzhuCouponList';
import styles from './index.module.scss';

const { TabPane } = Tabs;

export interface IselectDiscountCouponRef {
  /**
   * amount: 房价、以分为单位,
   * changedCouponId: 当前选择的券ID
   * callback: (value: 券ID，record: 券信息)
   */
  openModal: (value: {
    // 渠道类型
    channelType: SourceChannelType;
    businessDate?: string;
    wyuid: string;
    amount: number;
    occType?: OccType;
    id?: number; // 某房型某天房价的券ID
    changedCouponId?: Array<string>; // 已选择的券
    // 新加选住参数
    xuanzhu?: {
      remark?: string;
    };
    callback?: (value?: Array<string>, record?: Array<Partial<Icoupon>>) => void;
  }) => void;
  resetState: () => void;
  batchUpdateCacheCouponStatus: (...args) => void;
  clearCacheCouponStatusMap: () => void;
}

interface IcouponFn {
  getOptionalWangyuCouponListFn: (value: {
    sortType?: number;
    businessDate: string;
    wyId: string;
    amount: number;
    occType: OccType;
  }) => void;
  getOptionalManualCouponListFn: (value: { wyuid: string }) => void;
  saveDataFn: (..._) => void;
}

interface IselectDiscountCouponProps extends Partial<GuestInitialStateProps>, Partial<IcouponFn> {
  myRef?: any;
  onChange?: (value?: Array<string>, record?: Array<Partial<Icoupon>>) => void; // 确认回调
}

const SelectDiscountCoupon: React.FC<IselectDiscountCouponProps> = ({
  myRef,
  onChange,
  availableCouponList,
  optionalXuanzhuCouponList,
  optionalWangyuCouponListLoading,
  optionalXuanzhuCouponListLoading,
  getOptionalWangyuCouponListFn,
  getOptionalManualCouponListFn,
  saveDataFn,
}) => {
  // 前端临时选择的券状态
  const [cacheCouponStatusMap, setCacheCouponStatusMap] = useState<any>({});

  const [visible, setVisible] = useState<boolean>(false);
  const [customerInfo, setCustomerInfo] = useState<any>({});
  const [selectedId, setSelectedId] = useState<Array<string>>([]);
  // 是否使用、撤回过手工券
  const [isUpdateManualCoupon, setIsUpdateManualCoupon] = useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<string>('wangyu');

  const [sortType, setSortType] = useState<1 | 2>(1);

  const ref = useRef<any>();

  const bodyRef = useRef<any>();

  const currentList = useMemo(() => {
    return availableCouponList.map((item: any) => {
      if (!isNil(cacheCouponStatusMap[item.couponNo])) {
        const status = cacheCouponStatusMap[item.couponNo];
        return {
          ...item,
          status,
          disabled: status === CouponStatus.Locked,
          // couponAmount: status === CouponStatus.Locked ? item.couponAmount : undefined,
          // couponName: status === CouponStatus.Locked ? item.couponName : undefined,
          // couponType: status === CouponStatus.Locked ? item.couponType : undefined,
          // couponSubType: status === CouponStatus.Locked ? item.couponSubType : undefined,
          // couponWyuid: status === CouponStatus.Locked ? item.couponWyuid : undefined,
        };
      }
      return item;
    });
  }, [availableCouponList, cacheCouponStatusMap]);

  const openModal = (value) => {
    // 缓存参数
    ref.current = value;
    setVisible(true);
  };

  const getCustomerInfo = async (wyuid) => {
    // 查询用户信息
    try {
      const res = await fetchDesensitizedInfoByWyuid({ wyuid });
      if (res.code === 0) {
        setCustomerInfo(res.data);
      }
    } catch (error) {
      //
    }
  };

  const initCouponList = (sort = 1) => {
    const { businessDate, wyuid, amount, occType = OccType.DayUse, changedCouponId } =
      ref.current || {};
    if (!wyuid) {
      return;
    }

    getCustomerInfo(wyuid);

    // 网鱼券（根据券号计算金额）、手工券
    getOptionalWangyuCouponListFn({
      sortType: sort,
      businessDate,
      wyId: wyuid,
      amount,
      occType,
    });

    getOptionalManualCouponListFn({ wyuid });

    // 某天房价初始选中的券解锁
    changedCouponId?.forEach((value) => {
      setCacheCouponStatusMap({ ...cacheCouponStatusMap, [value]: CouponStatus.Effective });
    });

    // 当前选中的
    setSelectedId(changedCouponId || []);
  };

  const resetState = () => {
    ref.current = {};
    setVisible(false);
    setCustomerInfo({});
    setSelectedId([]);
    setIsUpdateManualCoupon(false);
    setSelectedTab('wangyu');
    setSortType(1);
  };

  const clearCacheCouponStatusMap = () => {
    setCacheCouponStatusMap({});
  };

  const handleOk = async () => {
    const { businessDate, wyuid, amount, occType = OccType.DayUse, callback } = ref.current;
    if (selectedTab === 'wangyu') {
      // 计算券金额
      const recordList = availableCouponList.filter((item) => selectedId?.includes(item.couponNo));
      const couponNo = get(recordList, '[0].couponNo', '');
      if (couponNo) {
        try {
          const res = await fetchCouponCalculate({
            businessDate,
            wyuid,
            occType,
            roomPrice: amount,
            lockCouponNo: couponNo,
          });
          if (res?.code === 0) {
            const { couponAmount } = res?.data || {};
            set(recordList, '[0].couponFaceValue', couponAmount);
          }
        } catch (error) {
          //
        }
        setCacheCouponStatusMap({ ...cacheCouponStatusMap, [couponNo]: CouponStatus.Locked });
      }
      // 回调
      resetState();
      onChange?.(selectedId, recordList);
      callback?.(selectedId, recordList);
    }

    if (selectedTab === 'xuanzhu') {
      resetState();
      if (isUpdateManualCoupon) {
        onChange?.();
        callback?.();
      }
    }
  };

  const handleCancel = () => {
    const { changedCouponId, callback } = ref.current || {};

    // 重置回初始状态
    if (Array.isArray(changedCouponId) && changedCouponId.length) {
      setCacheCouponStatusMap({
        ...cacheCouponStatusMap,
        [changedCouponId[0]]: CouponStatus.Locked,
      });
    }

    resetState();
    if (isUpdateManualCoupon) {
      onChange?.();
      callback?.();
    }
  };

  const handleTabChange = (key) => {
    setSelectedTab(key);
  };

  const handleSelect = (couponNo) => {
    // 单选且可取消
    if (selectedId.includes(couponNo)) {
      setSelectedId([]);
    } else {
      setSelectedId([couponNo]);
    }
  };

  const onSortChange = (sort) => {
    setSortType(sort);
    initCouponList(sort);
  };

  const onUpdateCoupon = (index, params) => {
    setIsUpdateManualCoupon(true);
    const list = [...optionalXuanzhuCouponList];
    if (list[index]) {
      list[index] = { ...list[index], ...params };
    }
    saveDataFn({
      optionalXuanzhuCouponList: list,
    });
  };

  useEffect(() => {
    return () => {
      clearCacheCouponStatusMap();
    };
  }, []);

  useEffect(() => {
    if (visible) {
      initCouponList();
    }
  }, [visible]);

  useEffect(() => {
    if (bodyRef.current) {
      const height = bodyRef.current.scrollHeight;
      if (height >= 648) {
        bodyRef.current.style.overflow = 'auto';
      } else {
        bodyRef.current.style.overflow = 'hidden';
      }
    }
  }, [currentList, optionalXuanzhuCouponList]);

  useImperativeHandle(
    myRef,
    () => ({
      openModal,
      resetState,
      batchUpdateCacheCouponStatus(couponStatusMap) {
        setCacheCouponStatusMap({ ...cacheCouponStatusMap, ...couponStatusMap });
      },
      clearCacheCouponStatusMap,
    }),
    [cacheCouponStatusMap],
  );

  const tabList: Array<ItabItem> = [
    {
      key: 'wangyu',
      tab: renderIntlLabel('guest_coupon_tab_wangyu_coupon', {
        number: currentList?.filter((item) => !item.disabled)?.length,
      }),
      component: (
        <WangyuCouponList
          wyuid={ref.current?.wyuid}
          customerInfo={customerInfo}
          channelType={ref.current?.channelType}
          selectedId={selectedId}
          currentList={currentList}
          wangyuCouponListLoading={optionalWangyuCouponListLoading}
          onSelect={handleSelect}
          onGiveCouponClose={initCouponList}
          sortType={sortType}
          onSortRadioChange={onSortChange}
        />
      ),
    },
    {
      key: 'xuanzhu',
      tab: renderIntlLabel('guest_coupon_tab_manual_coupon', {
        number: optionalXuanzhuCouponList?.length || 0,
      }),
      component: (
        <XuanzhuCouponList
          wyuid={ref.current?.wyuid}
          remark={ref.current?.xuanzhu?.remark}
          xuanzhuCouponList={optionalXuanzhuCouponList}
          xuanzhuCouponListLoading={optionalXuanzhuCouponListLoading}
          getOptionalManualCouponListFn={getOptionalManualCouponListFn}
          onUpdateCoupon={onUpdateCoupon}
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        wrapClassName={styles.container}
        title={renderIntlLabel('guest_title_select_of_coupon')}
        destroyOnClose
        maskClosable={false}
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <div ref={bodyRef}>
          <div className={styles.info}>
            <Form.Item label={renderIntlLabel('order_roomorder_name')}>
              {customerInfo?.name}
            </Form.Item>
            <Form.Item label={renderIntlLabel('order_roomorder_cert_no')}>
              {customerInfo?.certNo}
            </Form.Item>
            {/* <Form.Item label={renderIntlLabel('order_roomorder_mobile')}>
              {customerInfo?.mobile}
            </Form.Item> */}
          </div>
          <Tabs onChange={handleTabChange} type="card">
            {tabList?.map((item) => (
              <TabPane tab={item.tab} key={item.key}>
                {item.component}
              </TabPane>
            ))}
          </Tabs>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.guest,
  };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  getOptionalWangyuCouponListFn: getOptionalWangyuCouponList,
  getOptionalManualCouponListFn: getOptionalManualCouponList,
  saveDataFn: saveData,
})(React.memo(SelectDiscountCoupon));

export default React.forwardRef<IselectDiscountCouponRef, IselectDiscountCouponProps>(
  (props, ref) => <ConnectComponent {...props} myRef={ref} />,
);
